/* eslint-disable import/order */
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { isEmpty } from 'ramda';
import Select from 'react-select';
import { Formik, Field, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import { getTimeZones } from '@vvo/tzdb';

import { BookingStatus } from 'utils/steps';
import { useTeamMembersList } from 'utils/hooks';
import { customStyles } from 'components/FormComponents/MultiSelect';
import { getBookingCount } from '../utils';
import { bookingRequest } from 'utils/validationSchema';

import Icon from 'components/Icon';
import { StoreConsumer } from 'components/StoreProvider';
import SolidButton from 'components/SolidButton';
import Button from 'components/Button';
import { NoPatientData } from 'components/EmptyState';
import Modal from 'components/Modal';
import { TextAreaInput, MultiSelect, DateInput } from 'components/FormComponents';
import RejectModal from '../RejectModal';

import alert from 'assets/images/alert.png';

const statusList = {
  [BookingStatus.AWAITING]: 'Agendada',
  [BookingStatus.CONFIRMED]: 'Confirmada',
  [BookingStatus.REJECTED]: 'Rechazada',
  [BookingStatus.EXPIRED]: 'Vencida',
  [BookingStatus.CANCELED]: 'Cancelada',
  [BookingStatus.COMPLETED]: 'Finalizada',
  [BookingStatus.RESCHEDULED]: 'Re-agendada',
};

const modalProps = {
  content: {
    width: '95%',
    maxWidth: 414,
    minHeight: 414,
  },
};

const customMemberStyles = {
  option: (provided) => ({
    ...provided,
    fontWeight: '300',
    fontSize: '16px',
    color: '#606060',
    backgroundColor: '#fff',
    ':hover': {
      color: '#fff',
      backgroundColor: '#8093FC',
      cursor: 'pointer',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '14px',
  }),
};

function generateHours() {
  const hoursArray = [];
  const startHour = 0;
  const endHour = 23;

  for (let hour = startHour; hour <= endHour; hour += 1) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hourValue = `0${hour}`.slice(-2);
      const formattedHour = `0${hour % 12 === 0 ? 12 : hour % 12}`.slice(-2);
      const period = hour < 12 ? 'AM' : 'PM';
      const formattedMinute = `0${minute}`.slice(-2);
      const value = `${hourValue}:${formattedMinute}`;
      const time = `${formattedHour}:${formattedMinute} ${period}`;
      hoursArray.push({ value, label: time });
    }
  }

  return hoursArray;
}
const hours = generateHours();

const Calls = ({ patient = {}, onUpdateBooking = () => {}, onJoinCall = () => {} }) => {
  const { t } = useTranslation();
  const { bookings: baseBookings = [], currentBooking = {} } = { ...patient };
  const { id: currentId = '', status: currentStatus, startAt: scheduleDate } = { ...currentBooking };
  const { teamMembersOptions = [] } = useTeamMembersList();
  const [booking, setBooking] = useState();
  const [value, setValue] = useState();
  const [target, setTarget] = useState();
  const [updating, setUpdating] = useState(false);
  const [selectedMember, setSelectedMember] = useState('');

  const bookings = baseBookings.map((data) => {
    const { versions = [], startAt, status } = { ...data };
    const logs = (versions || []).map((version) => {
      const { logs: versionLog = [] } = { ...version };
      const { status = [], message = [], updated_at = [], start_at = [], session_id = [] } = { ...versionLog };
      return {
        status: (status || [])[1],
        message: (message || [])[1],
        updatedAt: (updated_at || [])[1],
        startAt: (start_at || [])[1],
        sessionId: (session_id || [])[1],
      };
    });
    const [first = {}] = [...logs];
    const sessionIdLog = status === 'confirmed' ? logs[logs.length - 1] : logs.find(({ status }) => !status);
    const { sessionId = '' } = { ...sessionIdLog };
    return {
      ...data,
      logs,
      updatedAt: first.updatedAt || startAt,
      sessionId,
    };
  });

  const { sessionId } = { ...bookings.find(({ id }) => id === currentId) };

  const selectOptions = bookings
    .sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())
    .map(({ id: value, updatedAt }) => {
      const formatDate = moment(updatedAt).format('DD / MM / YYYY');
      return {
        value,
        label: value === currentId ? `Activa (${formatDate})` : formatDate,
      };
    });

  const onSelectBooking = (item) => {
    const { value: bookingId } = { ...item };
    const selected = bookings.find(({ id }) => id === bookingId);
    setBooking(selected);
    setValue(item);
    setUpdating(false);
  };

  const { id: bookId, logs: allLogs = [] } = { ...booking };

  useEffect(() => {
    if (!isEmpty(bookings) && !bookId && !currentId) {
      const item = bookings[bookings.length - 1];
      const { id: value, updatedAt } = { ...item };
      onSelectBooking({ value, label: moment(updatedAt).format('DD / MM / YYYY') });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  useEffect(() => {
    if (currentId) {
      const item = bookings.find(({ id }) => id === currentId);
      const { id: value, updatedAt } = { ...item };
      onSelectBooking({ value, label: `Activa (${moment(updatedAt).format('DD / MM / YYYY')})` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, currentStatus]);

  const logs = (allLogs || []).filter(({ status, startAt }) => !!status || !!startAt);
  const isCall = bookId === currentId && currentStatus === BookingStatus.CONFIRMED && !!sessionId;

  const onCloseModal = () => setTarget(undefined);
  const onRejectModal = () => setTarget(BookingStatus.REJECTED);
  const onConfirmModal = () => setTarget(BookingStatus.CONFIRMED);
  const onCompleteModal = () => setTarget(BookingStatus.COMPLETED);
  const onCancelModal = () => setTarget(BookingStatus.CANCELED);
  const onRescheduleModal = () => setTarget(BookingStatus.RESCHEDULED);
  const onSubmit = (payload) => {
    setUpdating(true);
    onUpdateBooking(
      {
        ...payload,
        bookingId: currentId,
        status: target === BookingStatus.RESCHEDULED ? BookingStatus.CONFIRMED : target,
        ...(!!selectedMember && {
          ownerId: selectedMember,
          ownerType: 'TeamMember',
        }),
      },
      onCloseModal,
      setUpdating,
    );
  };

  const joinCall = (setStoreData) => async () => {
    onJoinCall(currentId, sessionId, { startAt: moment(scheduleDate).format('DD/MM/YYYY - hh:mma') }, setStoreData);
  };

  const bookingsCount = getBookingCount(baseBookings);
  const timeZones = getTimeZones();
  const formatTimeZones = timeZones.map((timeZone) => ({
    label: timeZone.currentTimeFormat,
    value: timeZone.currentTimeFormat,
  }));

  return (
    <StoreConsumer>
      {({ openCall, setStoreData }) => {
        const isOpenCall = openCall.isOpen;
        return (
          <div className="w-100">
            <div className="patient__title">
              <div>
                <Icon name="bell" className="icon" />
                <p className="title">Llamadas</p>
              </div>
            </div>
            <div className="patient__tab_content follow-up">
              {isEmpty(bookings) ? (
                <NoPatientData tab="calls" calls />
              ) : (
                <>
                  <div className="form__input-container form__input-container-select patient__booking-select">
                    <p className="patient__booking-select_length">
                      <strong>Número de teleconsultas:</strong> {bookingsCount}
                    </p>
                    <label>Solicitud:</label>
                    <Select
                      id="booking"
                      name="booking"
                      className="form__input-select"
                      options={selectOptions}
                      value={value}
                      onChange={onSelectBooking}
                      styles={customStyles}
                      placeholder="Solicitud activa"
                    />
                  </div>
                  <div className="patient__call-logs">
                    {logs.map((log, index) => {
                      const { updatedAt, id, status, message, startAt } = { ...log };
                      return (
                        <div className="item" key={id}>
                          <div className="info">
                            <p className="title">{`Registro ${index + 1}`}</p>
                            <p className="date">{moment(updatedAt).format('DD/MM/YYYY - hh:mma')}</p>
                          </div>
                          <div className="content">
                            <p className="message">
                              {message}
                              {!!message && <br />}
                              {!status && !!startAt && (
                                <span>
                                  {'Solicitud re-agendada para el '}
                                  <strong>{moment(startAt).format('DD/MM/YYYY - hh:mma')}</strong>
                                </span>
                              )}
                              {status === BookingStatus.CONFIRMED && (
                                <span>
                                  {index === logs.length - 1
                                    ? 'Solicitud confirmada para el '
                                    : 'Solicitud confirmada por el especialista'}
                                  {index === logs.length - 1 && (
                                    <strong>{moment(scheduleDate).format('DD/MM/YYYY - hh:mma')}</strong>
                                  )}
                                </span>
                              )}
                              {status === BookingStatus.AWAITING && (
                                <span>
                                  {'Solicitud agendada para el '}
                                  <strong>{moment(scheduleDate).format('DD/MM/YYYY - hh:mma')}</strong>
                                </span>
                              )}
                              {status === BookingStatus.CANCELED && (
                                <span>
                                  Solicitud <strong>cancelada</strong>
                                </span>
                              )}
                              {status === BookingStatus.COMPLETED && (
                                <span>
                                  {'Solicitud '}
                                  <strong>finalizada</strong>
                                  {' por el especialista'}
                                </span>
                              )}
                              {status === BookingStatus.EXPIRED && (
                                <span>
                                  {'La solicitud '}
                                  <strong>expiró</strong>
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="actions">
                            {status !== BookingStatus.AWAITING && (
                              <p className={`status ${status}`}>{statusList[status]}</p>
                            )}
                            {status === BookingStatus.AWAITING &&
                              (index === logs.length - 1 && !updating ? (
                                <>
                                  <Button category="rounded" onClick={onConfirmModal}>
                                    Aceptar
                                  </Button>
                                  <Button category="secondary-rounded" onClick={onRejectModal}>
                                    Rechazar
                                  </Button>
                                </>
                              ) : (
                                <p className={`status ${status}`}>{statusList[status]}</p>
                              ))}
                            {!status && !!startAt(<p className="status confirmed">{statusList.rescheduled}</p>)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {isCall && !updating && !isOpenCall && (
                    <div className="mx-2 button-container">
                      <Button category="rounded" onClick={joinCall(setStoreData)}>
                        Join the call
                      </Button>
                      <Button category="secondary-rounded" onClick={onRescheduleModal} className="button-call-complete">
                        Reschedule the call
                      </Button>
                      <Button category="secondary-rounded" onClick={onCancelModal} className="button-call-complete">
                        Cancel the call
                      </Button>
                      <Button category="secondary-rounded" onClick={onCompleteModal} className="button-call-complete">
                        Finish the call
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
            <RejectModal
              modalIsOpen={target === BookingStatus.REJECTED}
              onCloseModal={onCloseModal}
              onSubmit={onSubmit}
            />
            <Modal
              modalIsOpen={target === BookingStatus.CONFIRMED || target === BookingStatus.COMPLETED}
              closeModal={onCloseModal}
              closeable={false}
              modalProps={modalProps}
            >
              <div>
                <div className="alert-container">
                  <img src={alert} className="alert-icon" alt="alert-icon" />
                </div>
                <h3 className="modal__title">Gestionar Solicitud</h3>
                {target === BookingStatus.CONFIRMED && (
                  <p className="modal__subtitle">
                    ¿Realmente deseas confirmar la llamada para el{' '}
                    <strong>{moment(scheduleDate).format('DD/MM/YYYY - hh:mma')}</strong> ?
                  </p>
                )}
                {target === BookingStatus.COMPLETED && (
                  <>
                    <p className="modal__subtitle">
                      ¿Realmente deseas marcar la llamada como <strong>finalizada</strong> ?
                    </p>
                    <Select
                      placeholder="Selecciona el profesional que realizó la llamada"
                      options={teamMembersOptions}
                      styles={customMemberStyles}
                      maxMenuHeight={120}
                      onChange={({ value }) => setSelectedMember(value)}
                    />
                  </>
                )}
                <div className="handle-call-buttons">
                  <SolidButton className="secondary mr-2" onClick={onCloseModal}>
                    Cancelar
                  </SolidButton>
                  <SolidButton onClick={() => onSubmit()}>Aceptar</SolidButton>
                </div>
              </div>
            </Modal>
            <Modal
              modalIsOpen={target === BookingStatus.CANCELED}
              closeModal={onCloseModal}
              closeable={false}
              modalProps={modalProps}
            >
              <div>
                <div className="alert-container">
                  <img src={alert} className="alert-icon" alt="alert-icon" />
                </div>
                <h3 className="modal__title">Manage request</h3>
                <p className="modal__subtitle">Do you really want to cancel the call??</p>
                <Formik initialValues={{ message: '' }} onSubmit={onSubmit}>
                  {() => (
                    <Form>
                      <Field
                        type="text"
                        label="Message"
                        name="message"
                        className="call-message-field"
                        minRows={20}
                        component={TextAreaInput}
                      />
                      <div className="handle-call-buttons">
                        <SolidButton type="button" className="secondary mr-2" onClick={onCloseModal}>
                          Back
                        </SolidButton>
                        <SolidButton type="submit">Continue</SolidButton>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </Modal>
            <Modal
              modalProps={modalProps}
              modalIsOpen={target === BookingStatus.RESCHEDULED}
              closeModal={onCloseModal}
              closeable={false}
            >
              <h3 className="booking-title">{t('patient.personalInfo.booking.titleUpdate')}</h3>
              <Formik
                initialValues={{ date: '', hour: '', timeZone: '', message: '' }}
                onSubmit={onSubmit}
                validationSchema={bookingRequest}
                enableReinitialize
              >
                {({ isSubmitting, isValid }) => {
                  const isDisabledButton = [!isValid, isSubmitting].includes(true);
                  return (
                    <Form className="booking-form">
                      <Field label={t('formLabel.date')} name="date" validation="none" component={DateInput} />
                      <Field
                        type="text"
                        label={t('formLabel.hour')}
                        name="hour"
                        component={MultiSelect}
                        options={hours}
                      />
                      <Field
                        type="text"
                        label={t('formLabel.timeZone')}
                        name="timeZone"
                        component={MultiSelect}
                        options={formatTimeZones}
                      />
                      <Field
                        type="text"
                        label={t('formLabel.message')}
                        name="message"
                        minRows={4}
                        component={TextAreaInput}
                      />
                      <div className="booking-form__btns">
                        <SolidButton className="secondary" onClick={onCloseModal}>
                          {t('button.cancel')}
                        </SolidButton>
                        <SolidButton type="submit" className="primary" disabled={isDisabledButton}>
                          {t('button.update')}
                        </SolidButton>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </Modal>
          </div>
        );
      }}
    </StoreConsumer>
  );
};

export default Calls;
